'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

/**
 * A thin bar across the top that starts the instant an internal link is
 * clicked and completes when the new page arrives, so a click always shows
 * that it worked, even on a slow connection. No dependencies, one listener.
 */
export function NavProgress() {
  const bar = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.('a');
      if (!a || a.target === '_blank' || a.hasAttribute('download')) return;
      const href = a.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
      let url: URL;
      try {
        url = new URL(href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin || url.pathname === window.location.pathname) return;
      const el = bar.current;
      if (!el) return;
      el.dataset.state = 'idle';
      void el.offsetWidth; // restart the animation
      el.dataset.state = 'loading';
      clearTimeout(timer.current);
      timer.current = setTimeout(() => (el.dataset.state = 'done'), 10000);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  useEffect(() => {
    const el = bar.current;
    if (el && el.dataset.state === 'loading') {
      el.dataset.state = 'done';
      clearTimeout(timer.current);
    }
  }, [pathname]);

  return <div ref={bar} className="nav-progress" data-state="idle" aria-hidden="true" />;
}
